/**
 * AssessmentResults — persistent trait profile viewer.
 *
 * Accessible at any time via:
 *   /assessment-results            — returning student viewing profile
 *   /assessment-results?first=true — first-timer arriving from psychometric test
 */

import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  ChevronRight, RotateCcw, TrendingUp, ChevronDown, ChevronUp,
  Compass, Sparkles, Zap, Brain, Star, BarChart3, FlaskConical,
  BookOpen, Briefcase, Globe,
} from "lucide-react";
import { Sidebar } from "@/components/ui/sidebar";
import ProtectedRoute from "@/components/ProtectedRoute";
import { StudentStore } from "@/hooks/stores/StudentStore";
import { getClusterAverages, getDominantCluster } from "@/utils/traitScoring";
import traitList from "../../../traitList.json";

// ─── Cluster config ───────────────────────────────────────────────────────────

const CLUSTER_META: Record<string, {
  label: string; icon: React.ElementType;
  accent: string; accentBg: string; accentText: string;
  bar: string; ring: string;
  gradientStyle: string; track: string; scorePill: string;
  cardBg: string; cardBorder: string; iconBg: string; trackTinted: string;
}> = {
  STEM:       { label: "STEM & Technology",     icon: FlaskConical, accent: "text-blue-600",   accentBg: "bg-blue-50",   accentText: "text-blue-700",   bar: "bg-blue-500",   ring: "ring-blue-200",   gradientStyle: "linear-gradient(to right, #60a5fa, #2563eb)",   track: "bg-slate-100", scorePill: "bg-blue-50 text-blue-700",   cardBg: "bg-blue-50",   cardBorder: "border-blue-100",   iconBg: "bg-white",   trackTinted: "bg-blue-100" },
  Humanities: { label: "Arts & Humanities",     icon: BookOpen,     accent: "text-rose-600",   accentBg: "bg-rose-50",   accentText: "text-rose-700",   bar: "bg-rose-500",   ring: "ring-rose-200",   gradientStyle: "linear-gradient(to right, #fb7185, #e11d48)",   track: "bg-slate-100", scorePill: "bg-rose-50 text-rose-700",   cardBg: "bg-rose-50",   cardBorder: "border-rose-100",   iconBg: "bg-white",   trackTinted: "bg-rose-100" },
  Management: { label: "Business & Leadership", icon: Briefcase,    accent: "text-amber-600",  accentBg: "bg-amber-50",  accentText: "text-amber-700",  bar: "bg-amber-500",  ring: "ring-amber-200",  gradientStyle: "linear-gradient(to right, #fbbf24, #d97706)",   track: "bg-slate-100", scorePill: "bg-amber-50 text-amber-700", cardBg: "bg-amber-50",  cardBorder: "border-amber-100",  iconBg: "bg-white",   trackTinted: "bg-amber-100" },
  all:        { label: "Universal Skills",      icon: Globe,        accent: "text-emerald-600",accentBg: "bg-emerald-50",accentText: "text-emerald-700",bar: "bg-emerald-500",ring: "ring-emerald-200",gradientStyle: "linear-gradient(to right, #34d399, #059669)",   track: "bg-slate-100", scorePill: "bg-emerald-50 text-emerald-700",cardBg: "bg-emerald-50",cardBorder: "border-emerald-100",iconBg: "bg-white",   trackTinted: "bg-emerald-100"},
};

const CLUSTER_ORDER = ["STEM", "Humanities", "Management", "all"];

const TRAIT_FULL: Record<string, { name: string; cluster: string }> = Object.fromEntries(
  (traitList as Array<{ id: string; full_name: string; cluster: string }>).map((t) => [
    t.id,
    { name: t.full_name, cluster: t.cluster },
  ])
);

// ─── TraitBar ─────────────────────────────────────────────────────────────────

function TraitBar({ traitId, score, rank, compact = false }: {
  traitId: string; score: number; rank?: number; compact?: boolean;
}) {
  const info = TRAIT_FULL[traitId];
  const cluster = info?.cluster ?? "all";
  const meta = CLUSTER_META[cluster] ?? CLUSTER_META.STEM;

  if (compact) {
    return (
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-600 flex-1 truncate min-w-0">{info?.name ?? traitId}</span>
        <div className={`w-24 h-2 rounded-full ${meta.track} overflow-hidden shrink-0`}>
          <div
            className="h-2 rounded-full transition-all duration-700"
            style={{ width: `${score}%`, backgroundImage: meta.gradientStyle }}
          />
        </div>
        <span className={`text-xs font-bold w-7 text-right shrink-0 ${meta.accent}`}>{score}</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 min-w-0">
        {rank !== undefined && (
          <span className="text-xs font-semibold text-slate-300 w-5 shrink-0 text-right">#{rank}</span>
        )}
        <span className="text-sm text-slate-700 font-medium flex-1 truncate">{info?.name ?? traitId}</span>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${meta.scorePill}`}>
          {score}
        </span>
      </div>
      <div className={`w-full h-2.5 rounded-full ${meta.track} overflow-hidden`}>
        <div
          className="h-2.5 rounded-full transition-all duration-700"
          style={{ width: `${score}%`, backgroundImage: meta.gradientStyle }}
        />
      </div>
    </div>
  );
}

// ─── ClusterSection ───────────────────────────────────────────────────────────

function ClusterSection({ clusterId, traits }: { clusterId: string; traits: Array<{ traitId: string; score: number }> }) {
  const [open, setOpen] = useState(false);
  const meta = CLUSTER_META[clusterId] ?? CLUSTER_META.STEM;
  const Icon = meta.icon;
  const avg = Math.round(traits.reduce((s, t) => s + t.score, 0) / traits.length);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
      >
        <div className={`w-9 h-9 rounded-xl ${meta.accentBg} flex items-center justify-center shrink-0`}>
          <Icon className={`w-4.5 h-4.5 ${meta.accent}`} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className={`text-xs font-semibold uppercase tracking-wider ${meta.accent}`}>{meta.label}</p>
          <p className="text-slate-900 font-bold text-sm mt-0.5">
            {avg}<span className="text-slate-400 text-xs font-normal"> / 100 avg · {traits.length} traits</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className={`w-16 ${meta.track} rounded-full h-2 overflow-hidden hidden sm:block`}>
            <div className="h-2 rounded-full" style={{ width: `${avg}%`, backgroundImage: meta.gradientStyle }} />
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-3 space-y-2.5 border-t border-slate-100">
          {traits
            .sort((a, b) => b.score - a.score)
            .map(({ traitId, score }) => (
              <TraitBar key={traitId} traitId={traitId} score={score} compact />
            ))}
        </div>
      )}
    </div>
  );
}

// ─── ClusterScoreCard ─────────────────────────────────────────────────────────

function ClusterScoreCard({ cluster, avg }: { cluster: string; avg: number }) {
  const meta = CLUSTER_META[cluster] ?? CLUSTER_META.STEM;
  const Icon = meta.icon;
  return (
    <div className={`${meta.cardBg} rounded-2xl border ${meta.cardBorder} p-4 flex items-center gap-3`}>
      <div className={`w-10 h-10 rounded-xl ${meta.iconBg} flex items-center justify-center shrink-0 shadow-sm`}>
        <Icon className={`w-4.5 h-4.5 ${meta.accent}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-xs font-semibold truncate ${meta.accent}`}>{meta.label}</p>
        <div className="flex items-baseline gap-1.5 mt-0.5">
          <span className="text-2xl font-black text-slate-900">{avg}</span>
          <span className="text-xs text-slate-400">/ 100</span>
        </div>
        <div className={`mt-1.5 h-2 ${meta.trackTinted} rounded-full overflow-hidden`}>
          <div className="h-2 rounded-full transition-all duration-700" style={{ width: `${avg}%`, backgroundImage: meta.gradientStyle }} />
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AssessmentResults() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFirstTimer = searchParams.get("first") === "true";
  const student = StudentStore((s) => s.student);

  const { traitProfile, lastTaken } = useMemo(() => ({
    traitProfile: student?.traitProfile ?? null,
    lastTaken: student?.metadata?.lastAssessmentDate ?? null,
  }), [student]);

  const { dominantCluster, clusterAverages, topTraits, traitsByCluster } = useMemo(() => {
    if (!traitProfile) return { dominantCluster: null, clusterAverages: {}, topTraits: [], traitsByCluster: {} };

    const dominantCluster = getDominantCluster(traitProfile);
    const clusterAverages = getClusterAverages(traitProfile);
    const topTraits = Object.entries(traitProfile)
      .sort(([, a], [, b]) => (b as number) - (a as number))
      .slice(0, 8)
      .map(([traitId, score]) => ({ traitId, score: score as number }));

    const traitsByCluster: Record<string, Array<{ traitId: string; score: number }>> = {};
    for (const [traitId, score] of Object.entries(traitProfile)) {
      const cluster = TRAIT_FULL[traitId]?.cluster ?? "all";
      if (!traitsByCluster[cluster]) traitsByCluster[cluster] = [];
      traitsByCluster[cluster].push({ traitId, score: score as number });
    }

    return { dominantCluster, clusterAverages, topTraits, traitsByCluster };
  }, [traitProfile]);

  const dominant = dominantCluster ? (CLUSTER_META[dominantCluster] ?? CLUSTER_META.STEM) : null;
  const userName = student?.firstName
    ? `${student.firstName} ${student.lastName ?? ""}`.trim()
    : (student?.email ?? "Student");

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen" style={{ backgroundColor: "var(--theme-background)" }}>
        <Sidebar />

        <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
          {/* Mobile topbar */}
          <div className="md:hidden flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white sticky top-0 z-30">
            <Brain className="w-5 h-5 text-indigo-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <h1 className="text-base font-bold text-slate-900 truncate">Your Trait Profile</h1>
              <p className="text-xs text-slate-500 truncate">{userName}</p>
            </div>
          </div>

          <main className="flex-1 p-4 sm:p-6 lg:p-8">

            {/* ── No profile ── */}
            {!traitProfile && (
              <div className="max-w-lg mx-auto mt-8">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 flex flex-col items-center text-center gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center">
                    <Brain className="w-8 h-8 text-indigo-500" />
                  </div>
                  <div className="space-y-2">
                    <h1 className="text-2xl font-black text-slate-900">Unlock your profile</h1>
                    <p className="text-slate-500 text-sm leading-relaxed">
                      Take the psychometric assessment to discover your strengths and find careers that match who you are.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/psychometric-assessment")}
                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white font-bold rounded-xl transition-all flex items-center gap-2 shadow-sm"
                  >
                    <Sparkles className="w-4 h-4" /> Start Assessment
                  </button>
                </div>
              </div>
            )}

            {/* ── Profile exists ── */}
            {traitProfile && (
              <div className="xl:grid xl:grid-cols-[1fr_380px] xl:gap-6 space-y-6 xl:space-y-0 max-w-screen-2xl mx-auto">

                {/* ── Left column ── */}
                <div className="space-y-4 min-w-0">

                  {/* Page title — desktop only */}
                  <div className="hidden md:flex items-center justify-between">
                    <div>
                      <h1 className="text-2xl font-black text-slate-900">Trait Profile</h1>
                      <p className="text-slate-500 text-sm mt-0.5">
                        {userName} · 49 traits scored from your psychometric assessment
                        {lastTaken && (
                          <span className="ml-1.5 text-slate-400">
                            · Updated {new Date(lastTaken).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                          </span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate("/psychometric-assessment")}
                      className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:text-slate-800 transition-all shadow-sm"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Retake
                    </button>
                  </div>

                  {/* Hero banner */}
                  <div className="bg-gradient-to-br from-indigo-600 to-violet-700 rounded-2xl p-6 relative overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.08)_0%,_transparent_60%)] pointer-events-none" />
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-3">
                        <Brain className="w-4 h-4 text-white/60" />
                        <span className="text-white/70 text-xs font-medium uppercase tracking-wider">Dominant strength</span>
                      </div>
                      <h2 className="text-xl sm:text-2xl font-black text-white leading-tight">
                        {dominant ? (
                          <>Natural talent in <span className={`${dominant.accentText.replace("text-", "text-").replace("-700", "-200")}`}>{dominant.label}</span></>
                        ) : (
                          "Your unique strength map"
                        )}
                      </h2>
                      <p className="text-white/60 text-sm mt-2 leading-relaxed">
                        Your profile refines automatically as you complete courses, games, and assessments.
                      </p>
                    </div>
                  </div>

                  {/* First-timer CTA */}
                  {isFirstTimer && (
                    <div className="bg-white rounded-2xl border border-indigo-100 shadow-sm p-5 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                        <Compass className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-900 font-bold text-sm">Time to pick your career paths</p>
                        <p className="text-slate-500 text-xs mt-0.5 leading-relaxed">Your profile is ready. Explore paths that match your top traits.</p>
                      </div>
                      <button
                        onClick={() => navigate("/broad-path-selection", { state: { traitProfile } })}
                        className="shrink-0 flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white text-sm font-bold px-4 py-2 rounded-xl transition-all"
                      >
                        Explore <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Cluster score grid */}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Score by area</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-2 2xl:grid-cols-4 gap-3">
                      {CLUSTER_ORDER
                        .filter((c) => clusterAverages[c] !== undefined)
                        .map((cluster) => (
                          <ClusterScoreCard key={cluster} cluster={cluster} avg={clusterAverages[cluster] as number} />
                        ))}
                    </div>
                  </div>

                  {/* Top 8 traits */}
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-center gap-2.5 mb-5">
                      <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                        <TrendingUp className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="text-slate-900 font-bold text-sm">Top 8 Traits</h3>
                        <p className="text-slate-400 text-xs">Your highest-scoring strengths</p>
                      </div>
                      <div className="ml-auto flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                        <span className="text-xs text-slate-500 font-medium">Highest scores</span>
                      </div>
                    </div>
                    <div className="space-y-4">
                      {topTraits.map(({ traitId, score }, i) => (
                        <TraitBar key={traitId} traitId={traitId} score={score} rank={i + 1} />
                      ))}
                    </div>
                  </div>

                  {/* Growth note */}
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-start gap-4">
                    <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                      <Zap className="w-4 h-4 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-slate-800 font-semibold text-sm">Your profile grows over time</p>
                      <p className="text-slate-500 text-xs mt-1 leading-relaxed">
                        All 49 traits are scored 0–100. Complete courses, games, and quizzes to refine your profile and unlock better career matches.
                      </p>
                    </div>
                  </div>

                  {/* Retake — mobile only */}
                  <button
                    onClick={() => navigate("/psychometric-assessment")}
                    className="md:hidden w-full py-3 bg-white hover:bg-slate-50 border border-slate-200 rounded-2xl text-slate-600 hover:text-slate-800 text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-sm"
                  >
                    <RotateCcw className="w-4 h-4" /> Retake Assessment
                  </button>
                </div>

                {/* ── Right column — Full breakdown ── */}
                <div className="xl:sticky xl:top-6 xl:self-start space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center">
                      <BarChart3 className="w-3.5 h-3.5 text-violet-600" />
                    </div>
                    <h3 className="text-slate-900 font-bold text-sm">Full Breakdown</h3>
                    <span className="text-xs text-slate-400 ml-0.5">— tap to expand</span>
                  </div>

                  <div className="space-y-2">
                    {CLUSTER_ORDER.map((clusterId) => {
                      const traits = traitsByCluster[clusterId];
                      if (!traits || traits.length === 0) return null;
                      return <ClusterSection key={clusterId} clusterId={clusterId} traits={traits} />;
                    })}
                  </div>

                  {/* Quick-nav to career explorer */}
                  <button
                    onClick={() => navigate("/career-explorer")}
                    className="w-full flex items-center gap-3 p-4 bg-white hover:bg-slate-50 border border-slate-200 rounded-2xl transition-all shadow-sm text-left group"
                  >
                    <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0 group-hover:bg-indigo-100 transition-colors">
                      <Compass className="w-4.5 h-4.5 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-800 font-semibold text-sm">Explore career paths</p>
                      <p className="text-slate-400 text-xs mt-0.5">Find matches based on your profile</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-500 transition-colors shrink-0" />
                  </button>
                </div>

              </div>
            )}
          </main>
        </div>
      </div>
    </ProtectedRoute>
  );
}
